import { useCallback, useEffect, useMemo, useState } from "react";
import type { RajasthanGnaTgna } from "../../../models/rajasthan";
import { getGnaTgnaData } from "../../../Rest_api/RestApi";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import { FUNCTIONS } from "../const_raj";
import { R_CHART_TYPES } from "../const";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector } from "react-redux";
import SecondaryHeader from "../Demand/SecondaryHeader";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { SearchBox } from "../../../components/Search";
import { BrushStart } from "../../../models/chart_model";
import { renderHourTick } from "../../../components/recharts/components";
import { stringToConsistentColor } from "../../../extensions/string";
import { AddDatesWithFileName } from "../../../helpers/helper";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
const GROUP_BY = [
  {
    label: "State",
  },
  {
    label: "Utility",
  },
  {
    label: "IR Link",
  },
  {
    label: "Trader",
  },

  {
    label: "Generator",
  },
];

const KEYS = [
  {
    label: "Time Slot",
    key: "time",
    name: "Time Slot",
    firstKey: "time_block",
    secondKey: "date",
    values: ["time_block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    name: "Date",
    firstKey: "date",
    secondKey: undefined,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
];

const UNITS = ["MW", "MWh"];

export default function RajasthanGnaTgna() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [data, setData] = useState<RajasthanGnaTgna[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedKey, setSelectedKey] = useState(KEYS[0]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [loading, setLoading] = useState(true);
  const [selectedVariable, setSelectedVariable] = useState<"Buy" | "Sell">(
    "Buy"
  );
  const [selectedCategory, setSelectedCategory] = useState<
    "OA GNA" | "OA TGNA" | "Total"
  >("OA GNA");
  const [selectedFunction, setSelectedFunction] = useState(FUNCTIONS[0]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;

    const response = await getGnaTgnaData({
      startDate: convertToString(dates.startDate),
      endDate: convertToString(dates.endDate),
    });
    const processedGnaTgnaData = response.map((item) => {
      const isBuy = item.type__name === "Buyer";
      return {
        ...item,
        type: `${item.category__category} ${isBuy ? "Buy" : "Sell"}`,
        buy_sell: isBuy ? "Buy" : "Sell",
        value_mwh: Math.abs(item.value_mw) / 4,
        value_mw: Math.abs(item.value_mw),
        month: getMonthFromDate(item.date),
        generator_source: item.generator_source
          ? item.generator_source.charAt(0).toUpperCase() +
            item.generator_source.slice(1)
          : item.generator_source,
      };
    });

    setData(processedGnaTgnaData);
    setUniqueBlock(
      Array.from(new Set(processedGnaTgnaData.map((item) => item.time_block)))
    );
    const aa = processedGnaTgnaData.filter(
      (item) => item.generator_source == "undefined"
    );
    console.log(aa);
    setLoading(false);
  }, [DefaultDates, activeTab]);

  const getKeys = useCallback(() => {
    if (selectedKey.key === "time") {
      return ["date", "time_block"];
    } else if (selectedKey.key === "date") {
      return ["date"];
    } else {
      return [selectedKey.values[0]];
    }
  }, [selectedKey]);

  useEffect(() => {
    let filteredData = data;
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.time_block)
      );
    }

    if (selectedVariable === "Buy") {
      filteredData = filteredData.filter((item) => item.buy_sell === "Buy");
    } else {
      filteredData = filteredData.filter((item) => item.buy_sell === "Sell");
    }

    if (selectedCategory !== "Total") {
      filteredData = filteredData.filter(
        (item) => item.category__category === selectedCategory.replace(" ", "_")
      );
    }

    function getGroupByKey() {
      if (selectedGroupKeys.label === "State") {
        if (selectedVariable === "Buy") {
          return ["from_state__name", "to_state__name"];
        } else {
          return ["from_state__name", "to_state__name"];
        }
      }

      if (selectedGroupKeys.label === "Utility") {
        if (selectedVariable === "Buy") {
          return ["from_utility__name", "to_utility__name"];
        } else {
          return ["to_utility__name", "from_utility__name"];
        }
      }

      if (selectedGroupKeys.label === "IR Link") {
        if (selectedVariable === "Buy") {
          return ["ir_link__name"];
        } else {
          return ["ir_link__name"];
        }
      }

      if (selectedGroupKeys.label === "Trader") {
        if (selectedVariable === "Buy") {
          return ["trader__name"];
        } else {
          return ["trader__name"];
        }
      }

      if (selectedGroupKeys.label === "Category") {
        if (selectedVariable === "Buy") {
          return ["category__category"];
        } else {
          return ["category__category"];
        }
      }

      if (selectedGroupKeys.label === "Generator") {
        if (selectedVariable === "Buy") {
          return ["generator_source"];
        } else {
          return ["generator_source"];
        }
      }

      return [];
    }

    const groupByKeys = selectedFunction.comparative ? getGroupByKey() : [];
    if (selectedCategory !== "Total") {
      groupByKeys.push("category__category");
    }
    groupByKeys.push("buy_sell");

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["date", "time_block"],
      groupBy: groupByKeys,
      volumeKey: "",
      aggregationKeys: ["value_mwh", "value_mw"],
      averageKeys: ["value_mw"],
      averageDuplicationKeys: ["time_block", "date"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedGroupKeys,
    selectedFunction,
    getKeys,
    selectedBlock,
    selectedVariable,
    selectedCategory,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const handlexDataKey = useCallback(() => {
    if (selectedKey.key === "time") {
      if (selectedBlock.length > 0) {
        return "time_block";
      }
      return "time_block";
    } else {
      // if (selectedUnit !== DEMAND_UNITS[1]) {
      //   setSelectedUnit(DEMAND_UNITS[1]);
      // }
      return selectedKey.key;
    }
  }, [selectedBlock.length, selectedKey.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const summaryExtraKeys = selectedKey.isTime
    ? [
        "time_block", // Changed dateIndex to date
        selectedKey.values[1],
      ]
    : [selectedKey.values[0]];

  const summaryExtraLabels = selectedKey.values.map(
    (value) => (value === "date" ? "Date" : value) // Simplified label mapping
  );

  function getActiveLegends() {
    return chartKeys.map((key, index) => {
      let name = key;

      // Apply specific formatting only for State and Utility
      if (
        selectedGroupKeys.label === "State" ||
        selectedGroupKeys.label === "Utility"
      ) {
        name = name
          .replace("_", " -> ")
          .replaceAll("_", " ")
          .replace("Buy", " (Buy)")
          .replace("Sell", " (Sell)");
      } else {
        name = name
          .replaceAll("_", " ")
          .replaceAll("null", "Others")
          .replaceAll("undefined", " ")
          .replace("Buy", " (Buy)")
          .replace("Sell", " (Sell)");
      }

      return {
        dataKey: key + `_value_${selectedUnit.toLowerCase()}`,
        name: name,
        legendColor: undefined,
        stroke: stringToConsistentColor(key),
        type: selectedChartType.value,
        stackId: key.includes("Buy") ? "buy" : "sell",
      };
    });
  }

  const tablePar = {
    legends: getActiveLegends(),
    data: chartData || [],
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Power Procurement Gna/Tgna",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
  };

  function secondDataKey() {
    if (selectedKey.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }

  const handleKeyChange = (index: number) => {
    setSelectedKey(KEYS[index]);
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            items={KEYS.map((item) => item.name)}
            selectedItems={[selectedKey.name]}
            onSelect={(item, index) => {
              handleKeyChange(index);
            }}
            title={"Interval"}
          />

          <FilterMenu
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              setSelectedUnit(item);
            }}
            title={"Unit"}
          />
          <FilterMenu
            title="Function"
            items={FUNCTIONS.map((item) => item.label)}
            selectedItems={[selectedFunction.label]}
            onSelect={(item, index) => {
              setSelectedFunction(FUNCTIONS[index]);
            }}
          />
          <FilterMenu
            title="Variable"
            items={["Buy", "Sell"]}
            selectedItems={[selectedVariable]}
            onSelect={(item, index) => {
              setSelectedVariable(item as "Buy" | "Sell");
            }}
          />

          <FilterMenu
            title="Category"
            items={["Total", "OA GNA", "OA TGNA"]}
            selectedItems={[selectedCategory]}
            onSelect={(item, index) => {
              setSelectedCategory(item as "OA GNA" | "OA TGNA" | "Total");
            }}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={[selectedGroupKeys.label]}
            onSelect={(item, index) => {
              setSelectedGroupKeys(GROUP_BY[index]);
            }}
          />

          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(R_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">Volume Drawal (MW)</h2>
            <div className="flex gap-x-6  justify-end">
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  const secureTablePar = {
                    ...tablePar,
                    data: chartData || [],
                  };
                  dataTab.label === "Chart" &&
                    downloadChartTableCSV(secureTablePar);
                  dataTab.label === "Summary" &&
                    downloadSummaryCSV(secureTablePar);
                  dataTab.label === "Table" &&
                    downloadChartTableCSV(secureTablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              xDataKey={xDataKey}
              secondXDataKey={secondDataKey()}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              isTimeSlot={selectedKey.isTime}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              onlyTitle={true}
              brushIndex={{
                startIndex: 0,
                endIndex: 96 * 1,
              }}
              legends={getActiveLegends()}
              xTick={
                selectedKey.isTime && selectedBlock.length == 0
                  ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                  : undefined
              }
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<div>WBES</div>}
              text="
                1. WBES: OA GNA reports, OA TGNA reports
                2. MW: Represents the average (MW) when the selected time interval is Date.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
